import { useEffect, useState } from "react";
import { api, formatPrice } from "../lib/api";
import { Package, FolderTree, AlertTriangle, Star, DollarSign, Clock, ShoppingCart, Receipt } from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);

    useEffect(() => {
        api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    }, []);

    if (!stats) return <div className="p-10 font-mono text-sm">Cargando estadísticas…</div>;

    const cards = [
        { i: <Package className="w-5 h-5" />, t: "Productos", v: stats.total_products, sub: "Total en catálogo" },
        { i: <FolderTree className="w-5 h-5" />, t: "Categorías", v: stats.total_categories, sub: "Departamentos" },
        { i: <AlertTriangle className="w-5 h-5" />, t: "Stock bajo", v: stats.low_stock, sub: "≤ 5 unidades" },
        { i: <Star className="w-5 h-5" />, t: "Destacados", v: stats.featured, sub: "En home" },
    ];

    return (
        <div className="p-6 md:p-10" data-testid="admin-dashboard">
            <div className="border-b-2 border-[var(--lz-navy)] pb-4 mb-8">
                <span className="lz-section-label">Resumen</span>
                <h1 className="font-display text-4xl font-bold uppercase">Dashboard</h1>
            </div>

            <div className="mb-6 flex flex-wrap gap-3">
                <Link to="/admin/pos" className="lz-btn lz-btn-primary" data-testid="dash-go-pos">
                    <ShoppingCart className="w-4 h-4" /> Punto de venta
                </Link>
                <Link to="/admin/cortes" className="lz-btn lz-btn-outline" data-testid="dash-go-cortes">
                    <Receipt className="w-4 h-4" /> Cortes de caja
                </Link>
                {stats.cash_session_open && (
                    <span className="inline-flex items-center px-3 py-2 text-xs font-bold uppercase bg-green-100 text-green-800 border border-green-800">
                        Caja abierta
                    </span>
                )}
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                <div className="border-2 border-[var(--lz-navy)] bg-white p-5">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--lz-orange)]">Ventas hoy</div>
                    <div className="font-display text-3xl font-bold mt-1">{stats.sales_today_count ?? 0}</div>
                    <div className="text-xs text-[var(--lz-navy)]/60 mt-1">{formatPrice(stats.sales_today_total ?? 0)}</div>
                </div>
                <div className="border-2 border-[var(--lz-navy)] bg-white p-5">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--lz-orange)]">Turno de caja</div>
                    <div className="font-display text-xl font-bold mt-1 uppercase">
                        {stats.cash_session_open ? "Abierto" : "Cerrado"}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 border-2 border-[var(--lz-navy)] bg-white">
                {cards.map((c, idx) => (
                    <div key={c.t} className={`p-5 ${idx < cards.length - 1 ? "lg:border-r border-[var(--lz-navy)]/20" : ""} ${idx % 2 === 0 ? "border-r border-[var(--lz-navy)]/20 lg:border-r" : ""} ${idx < 2 ? "border-b border-[var(--lz-navy)]/20 lg:border-b-0" : ""}`} data-testid={`stat-card-${idx}`}>
                        <div className="flex items-center gap-2 text-[var(--lz-orange)]">{c.i}<span className="text-[10px] font-bold uppercase tracking-widest">{c.t}</span></div>
                        <div className="font-display text-4xl font-bold mt-2">{c.v}</div>
                        <div className="text-xs text-[var(--lz-navy)]/60 mt-1">{c.sub}</div>
                    </div>
                ))}
            </div>

            <div className="mt-8 grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 border-2 border-[var(--lz-navy)] bg-white">
                    <div className="bg-[var(--lz-navy)] text-white px-4 py-3 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-[var(--lz-orange)]" />
                        <span className="text-xs font-bold uppercase tracking-widest">Productos recientes</span>
                    </div>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-widest font-bold bg-[#f3f4f6]">
                                <th className="p-3 text-left">Producto</th>
                                <th className="p-3">Precio</th>
                                <th className="p-3">Stock</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#0f1419]/15">
                            {stats.recent_products.map((p) => (
                                <tr key={p.id} className="hover:bg-[#f3f4f6]">
                                    <td className="p-3"><Link to={`/admin/productos`} className="font-bold hover:text-[var(--lz-orange)]">{p.name}</Link></td>
                                    <td className="p-3 font-mono">{formatPrice(p.price)}</td>
                                    <td className="p-3 font-mono">{p.stock}</td>
                                </tr>
                            ))}
                            {stats.recent_products.length === 0 && (
                                <tr><td colSpan={3} className="p-6 text-center text-[var(--lz-navy)]/60">Sin productos</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="border-2 border-[var(--lz-navy)] bg-white">
                    <div className="bg-[var(--lz-navy)] text-white px-4 py-3 flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-[var(--lz-orange)]" />
                        <span className="text-xs font-bold uppercase tracking-widest">Inventario</span>
                    </div>
                    <div className="p-5">
                        <div className="text-xs uppercase tracking-widest text-[var(--lz-navy)]/60 font-bold">Valor total inventario</div>
                        <div className="font-display text-3xl font-bold mt-1" data-testid="inventory-value">{formatPrice(stats.inventory_value)}</div>
                        <div className="mt-5 text-xs uppercase tracking-widest text-[var(--lz-navy)]/60 font-bold">Por categoría</div>
                        <ul className="mt-2 space-y-1">
                            {stats.by_category.slice(0, 8).map((row) => (
                                <li key={row.category} className="flex justify-between text-sm border-b border-[var(--lz-navy)]/10 py-1">
                                    <span>{row.category}</span>
                                    <span className="font-mono font-bold">{row.count}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
}
